import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	Alert,
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Link,
	MenuItem,
	Pagination,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { RootState } from 'store';
import { isOperatorOrHigher, isSuperAdmin } from 'features/auth/helpers';
import { ISort } from 'features/common/types';
import { countriesWithAgencyConfig } from 'features/auth/constants';
import { segmentTypes } from 'features/salesOrder/types';
import {
	IPassenger,
	IReceipt,
	IReceiptFilters,
	PaymentReceivedPlatformTypeEnum,
	PaymentReceivedRelationTypeEnum,
} from '../../types';
import { getInvoiceablePassengersBySalesOrderId, getReceiptList, issueReceipt, reconcileReceipt } from '../../services';
import { ClientPaymentsFilters } from './ClientPaymentsFilters';
import { GenerateReceiptModal } from '../ClientPaymentsSection/GenerateReceiptModal';
import routes from 'constants/routes';
import { extractErrorMessage } from '../../../quotation/helpers';

// ─── Constantes ───────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [50, 100, 150, 200];
const DEFAULT_PAGE_SIZE = 50;

// ─── Labels ───────────────────────────────────────────────────────────────────

const RELATION_LABEL: Record<PaymentReceivedRelationTypeEnum, string> = {
	[PaymentReceivedRelationTypeEnum.MVOrderTransaction]: 'C2A / Tarjeta',
	[PaymentReceivedRelationTypeEnum.VNOrderTransaction]: 'VN Transacción',
	[PaymentReceivedRelationTypeEnum.CashPayment]: 'Efectivo',
	[PaymentReceivedRelationTypeEnum.WireTransferPayment]: 'Transferencia',
	[PaymentReceivedRelationTypeEnum.MVHotelReserve]: 'MV Hotel',
	[PaymentReceivedRelationTypeEnum.VNFlightReserve]: 'VN Vuelo',
	[PaymentReceivedRelationTypeEnum.VNHotelReserveMV]: 'VN Hotel MV',
};

const RELATION_COLOR: Record<
	PaymentReceivedRelationTypeEnum,
	'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'
> = {
	[PaymentReceivedRelationTypeEnum.MVOrderTransaction]: 'primary',
	[PaymentReceivedRelationTypeEnum.VNOrderTransaction]: 'secondary',
	[PaymentReceivedRelationTypeEnum.CashPayment]: 'warning',
	[PaymentReceivedRelationTypeEnum.WireTransferPayment]: 'info',
	[PaymentReceivedRelationTypeEnum.MVHotelReserve]: 'default',
	[PaymentReceivedRelationTypeEnum.VNFlightReserve]: 'default',
	[PaymentReceivedRelationTypeEnum.VNHotelReserveMV]: 'default',
};

const PLATFORM_LABEL: Record<PaymentReceivedPlatformTypeEnum, string> = {
	[PaymentReceivedPlatformTypeEnum.PaymentSimulator]: 'Tarjeta contra Aerolinea',
	[PaymentReceivedPlatformTypeEnum.SiemprePago]: 'SiemprePago',
	[PaymentReceivedPlatformTypeEnum.Bancard]: 'Bancard',
	[PaymentReceivedPlatformTypeEnum.ItauPoints]: 'Puntos Itaú',
	[PaymentReceivedPlatformTypeEnum.ATCRedEnlace]: 'ATC Red Enlace',
	[PaymentReceivedPlatformTypeEnum.Wompi]: 'Wompi',
	[PaymentReceivedPlatformTypeEnum.Cash]: 'Efectivo',
	[PaymentReceivedPlatformTypeEnum.WireTransfer]: 'Wire Transfer',
};

// agencyId → abreviatura BU
const BU_LABEL: Record<number, string> = Object.fromEntries(
	countriesWithAgencyConfig.map((c) => [c.agencyId, c.id as string]),
);

const SEGMENT_LABEL: Record<string, string> = Object.fromEntries(segmentTypes.map((s) => [s.id, s.name]));

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string): string => {
	try {
		return new Date(iso).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' });
	} catch {
		return iso;
	}
};

const formatAmount = (amount: number, currency: string): string =>
	`${new Intl.NumberFormat('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
		amount,
	)} ${currency}`;

// ─── CopyButton ───────────────────────────────────────────────────────────────

const CopyButton = ({ value }: { value: string }) => {
	const [copied, setCopied] = useState(false);
	return (
		<Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
			<IconButton
				size="small"
				onClick={() => {
					navigator.clipboard.writeText(value);
					setCopied(true);
					setTimeout(() => setCopied(false), 2000);
				}}
			>
				<ContentCopyIcon sx={{ fontSize: 13, color: copied ? 'success.main' : 'text.secondary' }} />
			</IconButton>
		</Tooltip>
	);
};

// ─── Componente principal ─────────────────────────────────────────────────────

export const ClientPaymentsList = (): JSX.Element => {
	const history = useHistory();
	const auth = useSelector((state: RootState) => state.auth);
	const canReconcile = isOperatorOrHigher(auth.user);
	const canUnreconcile = isSuperAdmin(auth.user);

	const [items, setItems] = useState<IReceipt[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [pageTotal, setPageTotal] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSize, setCurrentSize] = useState(DEFAULT_PAGE_SIZE);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [filters, setFilters] = useState<IReceiptFilters>({});
	const [sort, setSort] = useState<ISort[]>([{ field: 'createdAt', order: 'desc' }]);

	// ── Reconciliación por fila ───────────────────────────────────────────────
	const [reconcilingId, setReconcilingId] = useState<string | null>(null);

	// ── Generación de recibo ──────────────────────────────────────────────────
	const [loadingPassengersFor, setLoadingPassengersFor] = useState<string | null>(null);
	const [receiptPayment, setReceiptPayment] = useState<IReceipt | null>(null);
	const [receiptPassengers, setReceiptPassengers] = useState<IPassenger[]>([]);
	const [receiptModalOpen, setReceiptModalOpen] = useState(false);
	const [generatingReceipt, setGeneratingReceipt] = useState(false);

	const fetchData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await getReceiptList(currentPage, currentSize, filters, sort);
			setItems(res.data.elements);
			setTotalCount(res.data.total);
			setPageTotal(Math.ceil(res.data.total / currentSize));
		} catch {
			alert(extractErrorMessage(error, 'No se pudieron cargar los cobros.'));
		} finally {
			setLoading(false);
		}
	}, [currentPage, currentSize, filters, sort]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleFilter = (newFilters: IReceiptFilters) => {
		setFilters(newFilters);
		setCurrentPage(1);
	};

	const handleGoToOrder = (saleOrderId: string) =>
		history.push(routes.salesOrder.edit.replace(':id', saleOrderId) + '?section=cobros-facturas');

	// ── Sort ─────────────────────────────────────────────────────────────────

	const handleSort = (field: string) => {
		setSort((prev) => {
			const current = prev[0];
			if (current?.field === field) {
				return [{ field, order: current.order === 'asc' ? 'desc' : 'asc' }];
			}
			return [{ field, order: 'desc' }];
		});
		setCurrentPage(1);
	};

	const sortDirection = (field: string): 'asc' | 'desc' | undefined => {
		const s = sort[0];
		return s?.field === field ? s.order : undefined;
	};

	// ── Reconciliación ────────────────────────────────────────────────────────

	const handleReconcile = async (item: IReceipt, checked: boolean) => {
		setReconcilingId(item.id);
		try {
			const res = await reconcileReceipt(item.id, checked);
			setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ...res.data } : i)));
		} catch (error) {
			alert(extractErrorMessage(error, 'Error al actualizar la conciliación.'));
		} finally {
			setReconcilingId(null);
		}
	};

	// ── Generación de recibo ──────────────────────────────────────────────────

	const handleGenerateReceiptClick = async (item: IReceipt) => {
		if (item.saleOrderId) {
			setLoadingPassengersFor(item.id);
			try {
				const { data: invoiceablesPassengers } = await getInvoiceablePassengersBySalesOrderId(item.saleOrderId);
				if (invoiceablesPassengers.length > 0) {
					if (invoiceablesPassengers.length === 1) {
						const pax = invoiceablesPassengers[0];
						await doGenerateReceipt(item, pax.id || pax.keyId);
					} else {
						setReceiptPayment(item);
						setReceiptPassengers(invoiceablesPassengers);
						setReceiptModalOpen(true);
					}
				} else {
					setError('La orden no tiene pasajeros facturables');
				}
			} catch {
				setError(extractErrorMessage(error, 'Error al obtener los datos de la orden.'));
			} finally {
				setLoadingPassengersFor(null);
			}
		}
	};

	const doGenerateReceipt = async (item: IReceipt, passengerId: string) => {
		setGeneratingReceipt(true);
		try {
			const res = await issueReceipt(item.id, passengerId);
			setItems((prev) =>
				prev.map((i) =>
					i.id === item.id ? { ...i, issued: true, pdfUrl: res.data.pdfUrl, receiptNumber: res.data.receiptNumber } : i,
				),
			);
			setReceiptModalOpen(false);
			setReceiptPayment(null);
		} catch (error) {
			setError(extractErrorMessage(error, 'Error al generar el recibo.'));
		} finally {
			setGeneratingReceipt(false);
		}
	};

	const handleModalConfirm = (passengerId: string) => {
		if (receiptPayment) doGenerateReceipt(receiptPayment, passengerId);
	};

	// ─────────────────────────────────────────────────────────────────────────

	return (
		<Box>
			<Typography variant="h5" fontWeight="bold" mb={1}>
				Recibos de Clientes
			</Typography>

			<ClientPaymentsFilters onFilter={handleFilter} />

			{loading && (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			)}

			{!loading && error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{!loading && (
				<>
					<Box mb={1}>
						<Typography variant="body2" color="text.secondary">
							{totalCount} resultado{totalCount !== 1 ? 's' : ''}
						</Typography>
					</Box>

					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow sx={{ backgroundColor: 'grey.50' }}>
									<TableCell sx={{ fontWeight: 'bold' }}>
										<TableSortLabel
											active={sort[0]?.field === 'createdAt'}
											direction={sortDirection('createdAt') ?? 'desc'}
											onClick={() => handleSort('createdAt')}
										>
											Fecha
										</TableSortLabel>
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Orden</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Pasajero</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>BU</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Segmento</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Plataforma</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">
										<TableSortLabel
											active={sort[0]?.field === 'transactionAmount'}
											direction={sortDirection('transactionAmount') ?? 'desc'}
											onClick={() => handleSort('transactionAmount')}
										>
											Monto de la transacción
										</TableSortLabel>
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">
										<TableSortLabel
											active={sort[0]?.field === 'amountInMainCurrency'}
											direction={sortDirection('amountInMainCurrency') ?? 'desc'}
											onClick={() => handleSort('amountInMainCurrency')}
										>
											USD
										</TableSortLabel>
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Auth ID</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Referencia</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Observación</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="center">
										Conciliado
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Nro. Recibo</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="center">
										Recibo
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{items.length === 0 ? (
									<TableRow>
										<TableCell colSpan={15} align="center" sx={{ py: 5, color: 'text.secondary' }}>
											No se encontraron cobros con los filtros seleccionados.
										</TableCell>
									</TableRow>
								) : (
									items.map((item) => {
										const isReconcilingThis = reconcilingId === item.id;
										const isLoadingReceiptThis = loadingPassengersFor === item.id;
										return (
											<TableRow key={item.id} hover>
												{/* Fecha */}
												<TableCell sx={{ whiteSpace: 'nowrap' }}>
													<Typography variant="caption">{formatDate(item.createdAt)}</Typography>
												</TableCell>

												{/* Orden */}
												<TableCell>
													{item.saleOrderId && (
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
															<Link
																component="button"
																variant="caption"
																onClick={() => handleGoToOrder(item.saleOrderId || '')}
																sx={{ fontWeight: 'medium' }}
															>
																{item.saleOrderId.slice(0, 8)}
															</Link>
															<CopyButton value={item.saleOrderId} />
														</Box>
													)}
													{!item.saleOrderId && (
														<Typography variant="caption" noWrap sx={{ maxWidth: 140, display: 'block' }}>
															{'—'}
														</Typography>
													)}
												</TableCell>

												{/* Pasajero */}
												<TableCell>
													<Typography variant="caption" noWrap sx={{ maxWidth: 140, display: 'block' }}>
														{item.mainPassengerName ?? '—'}
													</Typography>
												</TableCell>

												{/* BU */}
												<TableCell>
													{item.agencyId != null && (
														<Chip label={BU_LABEL[item.agencyId] ?? item.agencyId} size="small" variant="outlined" />
													)}
												</TableCell>

												{/* Segmento */}
												<TableCell>
													{item.segmentType && (
														<Chip
															label={SEGMENT_LABEL[item.segmentType] ?? item.segmentType}
															size="small"
															variant="outlined"
															color="default"
														/>
													)}
												</TableCell>

												{/* Tipo */}
												<TableCell>
													<Chip
														label={RELATION_LABEL[item.relationType] ?? item.relationType}
														color={RELATION_COLOR[item.relationType] ?? 'default'}
														size="small"
													/>
												</TableCell>

												{/* Plataforma */}
												<TableCell>
													<Typography variant="caption">
														{PLATFORM_LABEL[item.platformType] ?? item.platformType}
													</Typography>
												</TableCell>

												{/* Monto local */}
												<TableCell align="right">
													<Typography variant="caption" fontWeight="medium" noWrap>
														{formatAmount(item.transactionAmount, item.transactionCurrency)}
													</Typography>
												</TableCell>

												{/* USD */}
												<TableCell align="right">
													<Typography variant="caption" fontWeight="medium">
														{item.amountInMainCurrency.toFixed(2)}
													</Typography>
												</TableCell>

												{/* Auth ID */}
												<TableCell>
													{item.authorizationId ? (
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
															<Typography variant="caption" fontFamily="monospace">
																{item.authorizationId}
															</Typography>
															<CopyButton value={String(item.authorizationId)} />
														</Box>
													) : (
														<Typography variant="caption" color="text.disabled">
															—
														</Typography>
													)}
												</TableCell>

												{/* Referencia */}
												<TableCell>
													<Tooltip title={item.extPaymentReference ?? item.extBankName ?? ''} arrow placement="top">
														<Typography
															variant="caption"
															noWrap
															sx={{
																maxWidth: 120,
																display: 'block',
																cursor: 'help',
															}}
														>
															{item.extPaymentReference ?? item.extBankName ?? '—'}
														</Typography>
													</Tooltip>
												</TableCell>

												{/* Observación */}
												<TableCell>
													{item.observations ? (
														<Tooltip title={item.observations} arrow placement="top">
															<Typography
																variant="caption"
																noWrap
																sx={{
																	maxWidth: 140,
																	display: 'block',
																	cursor: 'help',
																}}
															>
																{item.observations}
															</Typography>
														</Tooltip>
													) : (
														<Typography variant="caption">—</Typography>
													)}
												</TableCell>

												{/* Conciliado */}
												<TableCell align="center" sx={{ py: 0 }}>
													{isReconcilingThis ? (
														<CircularProgress size={16} />
													) : item.reconciled ? (
														<Tooltip title={item.reconciledBy ? `Conciliado por ${item.reconciledBy}` : 'Conciliado'}>
															<span>
																<Checkbox
																	checked
																	onChange={() => handleReconcile(item, false)}
																	disabled={!canUnreconcile}
																	size="small"
																	color="success"
																	checkedIcon={<CheckCircleOutlineIcon />}
																	sx={{ p: 0.5 }}
																/>
															</span>
														</Tooltip>
													) : (
														<Tooltip title={canReconcile ? 'Marcar conciliado' : 'Sin permiso'}>
															<span>
																<Checkbox
																	checked={false}
																	onChange={() => handleReconcile(item, true)}
																	disabled={!canReconcile}
																	size="small"
																	color="success"
																	icon={<RadioButtonUncheckedIcon />}
																	checkedIcon={<CheckCircleOutlineIcon />}
																	sx={{ p: 0.5 }}
																/>
															</span>
														</Tooltip>
													)}
												</TableCell>

												{/* Nro. Recibo */}
												<TableCell>
													<Typography
														variant="caption"
														fontFamily="monospace"
														color={item.issued ? 'text.primary' : 'text.disabled'}
													>
														{item.receiptNumber}
													</Typography>
												</TableCell>

												{/* Acción recibo */}
												<TableCell align="center" sx={{ py: 0 }}>
													{item.issued && item.pdfUrl ? (
														<Tooltip title={`Ver recibo ${item.receiptNumber}`}>
															<IconButton
																size="small"
																component="a"
																href={item.pdfUrl}
																target="_blank"
																rel="noopener noreferrer"
															>
																<OpenInNewIcon sx={{ fontSize: 16, color: 'primary.main' }} />
															</IconButton>
														</Tooltip>
													) : isLoadingReceiptThis ? (
														<CircularProgress size={16} />
													) : (
														item.saleOrderId && (
															<Tooltip title="Emitir recibo">
																<IconButton size="small" onClick={() => handleGenerateReceiptClick(item)}>
																	<ReceiptIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
																</IconButton>
															</Tooltip>
														)
													)}
												</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{/* ── Paginación estándar ── */}
					{pageTotal > 0 && (
						<Grid container justifyContent="center" flexDirection="row" padding={2} alignItems="center">
							<Grid item xs={12} md={1} textAlign="center">
								<Select
									value={currentSize}
									size="small"
									onChange={(e) => {
										setCurrentSize(Number(e.target.value));
										setCurrentPage(1);
									}}
								>
									{PAGE_SIZE_OPTIONS.map((v) => (
										<MenuItem key={v} value={v}>
											{v}
										</MenuItem>
									))}
								</Select>
							</Grid>
							<Grid item xs={12} md={6} marginTop={{ xs: 1, md: 0 }}>
								<Pagination
									count={pageTotal}
									page={currentPage}
									onChange={(_, p) => setCurrentPage(p)}
									color="primary"
								/>
							</Grid>
						</Grid>
					)}
				</>
			)}

			{/* Modal selección de pasajero facturable */}
			<GenerateReceiptModal
				open={receiptModalOpen}
				invoiceablePassengers={receiptPassengers}
				loading={generatingReceipt}
				onConfirm={handleModalConfirm}
				onClose={() => {
					setReceiptModalOpen(false);
					setReceiptPayment(null);
					setReceiptPassengers([]);
				}}
			/>
		</Box>
	);
};
